///
/// EarthBrowser - Radar Browser
///
/// Modern movable window for browsing NEXRAD radar stations using local OGC sync data
/// Created by Matt Giger on 1/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { NexradDataProvider } from '@/data-providers/NexradDataProvider'
import { NexradTable, type NexradStationFeature } from '@/earthbrowser/dataset-distribution/nexrad/NexradTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { Radio } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface RadarBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const RadarBrowserRenderer = memo(function RadarBrowserRenderer({
	windowId,
	windowState,
}: RadarBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const radarVisible = visibleDatasets.includes("nexrad")

	// Get dataset layer manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state for data
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [stations, setStations] = useState<NexradStationFeature[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Kept in a ref so the provider's queryFn always reads the current rows
	// instead of the stale closure it was created with
	const entriesRef = useRef<NexradStationFeature[]>([])

	// Load stations from the local table (mount, sync refresh, manual refresh)
	const loadStations = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const loadedStations = await NexradTable.getStationsWithScans()
			entriesRef.current = loadedStations
			setStations(loadedStations)
			setTotalCount(loadedStations.length)
			setRefreshTrigger(prev => prev + 1)
		} catch (error) {
			console.error('Failed to load radar stations:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [])

	// Load stations on mount
	useEffect(() => {
		void loadStations(true)
	}, [loadStations])

	// Reload silently whenever the nexrad layer refreshes after a sync, so the
	// scan listings don't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('nexrad', () => {
		void loadStations(false)
	})

	// Create a map of station_id to properties (for lookup)
	const stationMap = useMemo(() => {
		const map = new Map<string, NexradStationFeature>()
		for (const station of stations) {
			map.set(station.station_id, station)
		}
		return map
	}, [stations])

	// Initialize and update data provider
	useEffect(() => {
		// Don't do anything while loading
		if (isLoading) return

		// Only create provider if not initialized
		if (!isInitialized) {
			// Dispose old provider
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new NexradDataProvider({
				id: windowId,
				name: 'Radar Stations',
				// Read through the ref so refreshData() always sees current rows
				queryFn: async () => entriesRef.current
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			// Update existing provider with new data
			const provider = DataProviderRegistry.get(windowId) as NexradDataProvider
			if (provider) {
				void provider.refreshData()
			}
		}
	}, [stations, windowId, isLoading, isInitialized])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('nexrad', radarVisible)
		}
	}, [layerManager, radarVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle station row click
	const handleRowClick = useCallback((row: DataRow) => {
		const station = row as unknown as NexradStationFeature
		const featureId = station.station_id

		// Look up the station to verify it exists
		const feature = stationMap.get(featureId)
		if (!feature) {
			console.warn('Could not find radar station for:', featureId)
			return
		}

		// Ensure radar stations are visible on the map
		if (!radarVisible) {
			dispatch(sendDatasetAction({
				type: "nexrad",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('nexrad', true)
			}
		}

		// Directly call layer manager to focus on the radar station
		if (layerManager) {
			layerManager.focusSceneObject('nexrad', featureId)
		} else {
			console.warn('[RadarBrowser] Layer manager not available')
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, radarVisible, stationMap, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadStations(true)
	}, [loadStations])

	// Header actions
	const headerActions = useMemo(() => {
		const totalScans = stations.reduce((sum, s) => sum + s.scans.length, 0)
		return (
			<div className="flex items-center gap-2 p-1">
				<div className="flex items-center gap-1 bg-black/30 rounded-full px-3 py-1 border border-white/10">
					<span className="text-xs text-white/60">{totalScans} scans from {stations.length} stations</span>
				</div>
			</div>
		)
	}, [stations])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized && !isLoading) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`radar-browser-${refreshTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search radar stations by ID or name..."
				/>
			)
		}

		// Loading state
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-green-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-green-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Radar Stations
					</p>
					<p className="text-sm text-white/60">
						{isLoading ? 'Fetching data...' : 'Initializing...'}
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, isLoading, handleRowClick, handleRefresh, totalCount, refreshTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Radio className="w-5 h-5" />}
			iconColor="text-green-400"
			headerActions={headerActions}
			titlebarPrefix={<DatasetVisibilityToggle windowType="radar" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
